import React from "react";

import { IStore } from "reducers";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { resetProofWarning, setLineItemEdit } from "actions/customization-actions";
import { IEditItemPayload } from "reducers/customization-reducers";
import { format } from "lib/globalize-util";
import { makeStyles } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import { DialogHeader } from "components/dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Button from "@material-ui/core/Button";
import theme from "styles/theme";
import { DATATEST } from "constants/integration";

const { CUSTOMIZATIONS } = DATATEST;

interface IProps {
    open: boolean;
    onClose: () => void;
}

interface IStateProps {
    proofWarning: IEditItemPayload;
}

interface IDispatchProps {
    onResetProofWarning: () => void;
    onSetLineItemEdit: (payload: IEditItemPayload) => void;
}

const ProofRemoveWarningModal: React.FC<IProps & IStateProps & IDispatchProps> = ({
    open,
    onClose,
    proofWarning,
    onSetLineItemEdit,
    onResetProofWarning,
}) => {
    const classes = useStyles({});

    const handleConfirm = () => {
        onClose();
        onSetLineItemEdit(proofWarning);
        onResetProofWarning();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                classes={{ paper: classes.paper }}
                data-test={CUSTOMIZATIONS.EDIT_CUSTOMIZATION_PROOF_MODAL}
            >
                <DialogHeader
                    className={classes.dialogHeader}
                    title={<span className={classes.dialogTitle}>{format("Edit Customization")}?</span>}
                    onClose={onClose}
                    data-test={CUSTOMIZATIONS.EDIT_CUSTOMIZATION_PROOF_MODAL_HEADER}
                />

                <DialogContent className={classes.dialogBodyContainer}>
                    {format(
                        "Editing the product customization will remove the current Customization Proof from your order. If no changes are made, the Customization Proof will remain."
                    )}
                </DialogContent>
                <DialogActions className={classes.actions} disableSpacing={true}>
                    <Button
                        classes={{ root: classes.button }}
                        variant="outlined"
                        onClick={onClose}
                        data-test={CUSTOMIZATIONS.EDIT_CUSTOMIZATION_PROOF_MODAL_CANCEL_BUTTON}
                    >
                        {format("Cancel")}
                    </Button>
                    <Button
                        classes={{ root: classes.confirmButton }}
                        variant="outlined"
                        onClick={handleConfirm}
                        data-test={CUSTOMIZATIONS.EDIT_CUSTOMIZATION_PROOF_MODAL_EDIT_BUTTON}
                    >
                        {format("Edit Customization")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const useStyles = makeStyles({
    dialogBodyContainer: {
        display: "flex",
        fontSize: "14px",
    },
    dialogHeader: {
        border: "none",
        fontSize: "25px",
        fontWeight: "bold",
    },
    paper: {
        maxWidth: "400px",
        height: "250px",
        borderRadius: "10px",
    },
    actions: {
        padding: 24,
    },
    button: {
        height: 40,
        borderRadius: "4px",
        fontSize: 13,
        fontFamily: "inherit",
        textTransform: "initial",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    confirmButton: {
        height: 40,
        borderRadius: "4px",
        fontSize: 13,
        fontFamily: "inherit",
        textTransform: "initial",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: theme.palette.primary1Color,
        color: theme.palette.white,
        marginLeft: 20,
    },
    dialogTitle: {
        display: "flex",
        alignItems: "center",
    },
});

const mapStateToProps = (store: IStore): IStateProps => {
    const { customization } = store;
    const { proofWarning } = customization;

    return {
        proofWarning,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<{}>): IDispatchProps => ({
    onSetLineItemEdit: (payload) => dispatch(setLineItemEdit(payload)),
    onResetProofWarning: () => dispatch(resetProofWarning()),
});

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps
)(ProofRemoveWarningModal);
